/**
 * SpineControlBar — control strip for pause/resume/stop during active runs.
 *
 * Renders below the spine tab when a run is in progress (mid-run, pausing, paused).
 * Shows pause/resume toggle and stop button, mirroring the TabBar's layout.
 *
 * SRP: pure presentation. Receives run state + callbacks; emits control intents.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { useCallback, useRef, useState } from "react";
import { fonts, inkColors, statusColors, surfaceColors } from "../tokens";

interface SpineControlBarProps {
  /** Current run state — determines which controls render and their labels. */
  runState: "mid-run" | "pausing" | "paused";
  /** Handler for pause intent (mid-run/approval). */
  onPause?: () => void;
  /** Handler for resume intent (paused). */
  onResume?: () => void;
  /** Handler for opening kill confirmation dialog. */
  onShowKillConfirmation?: () => void;
  /** When true, disable all controls (e.g., during a pause/resume in-flight). */
  disabled?: boolean;
}

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexShrink: 0,
    background: surfaceColors.paper,
    borderBottom: `1px solid ${surfaceColors.line}`,
    padding: "8px 12px",
    gap: "8px",
    fontFamily: fonts.body,
    alignItems: "center",
  },
  button: {
    background: "transparent",
    border: "none",
    borderRadius: "4px",
    color: inkColors.ink2,
    cursor: "pointer",
    fontFamily: fonts.body,
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.3px",
    padding: "6px 10px",
    textTransform: "uppercase",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    position: "relative",
    flexShrink: 0,
    transition: "all 150ms",
    "&:active": {
      opacity: 0.6,
    },
    "&:disabled": {
      opacity: 0.5,
      cursor: "not-allowed",
    },
  },
  pauseButton: {
    "&:hover": {
      color: statusColors.warn,
    },
  },
  resumeButton: {
    "&:hover": {
      color: statusColors.warn,
    },
  },
  killButton: {
    "&:hover": {
      color: statusColors.danger,
    },
  },
  spacer: {
    flex: 1,
  },
  overlay: {
    position: "fixed",
    inset: "0",
    background: "rgba(0, 0, 0, 0.5)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
  },
  dialog: {
    background: surfaceColors.paper,
    borderRadius: "8px",
    boxShadow: "0 12px 40px rgba(0, 0, 0, 0.16)",
    padding: "24px",
    maxWidth: "480px",
    width: "100%",
    fontFamily: fonts.body,
    border: "none",
    margin: "auto",
  },
  dialogTitle: {
    fontSize: "18px",
    fontWeight: 700,
    color: inkColors.ink,
    marginBottom: "12px",
    lineHeight: "1.4",
  },
  dialogDescription: {
    fontSize: "13px",
    fontWeight: 400,
    color: inkColors.ink2,
    marginBottom: "16px",
    lineHeight: "1.5",
  },
  dimmedMessage: {
    fontSize: "12px",
    fontWeight: 400,
    color: inkColors.ink4,
    background: "#F5F5F5",
    padding: "12px",
    borderRadius: "4px",
    marginBottom: "20px",
    lineHeight: "1.5",
    borderLeft: `3px solid ${statusColors.warn}`,
  },
  dialogButtonGroup: {
    display: "flex",
    gap: "8px",
    justifyContent: "flex-end",
  },
  dialogButton: {
    fontFamily: fonts.body,
    fontSize: "12px",
    fontWeight: 600,
    padding: "8px 16px",
    borderRadius: "4px",
    border: "none",
    cursor: "pointer",
    textTransform: "uppercase",
    letterSpacing: "0.3px",
    transition: "all 150ms",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
  },
  dialogCancelButton: {
    background: "transparent",
    color: inkColors.ink2,
    border: `1px solid ${surfaceColors.line}`,
    "&:hover": {
      color: inkColors.ink,
      background: surfaceColors.paperDim,
    },
    "&:active": {
      opacity: 0.6,
    },
  },
  dialogConfirmButton: {
    background: statusColors.danger,
    color: "#fff",
    "&:hover": {
      opacity: 0.9,
    },
    "&:active": {
      opacity: 0.8,
    },
    "&:disabled": {
      opacity: 0.5,
      cursor: "not-allowed",
    },
  },
});

export const SpineControlBar: React.FC<SpineControlBarProps> = ({
  runState,
  onPause,
  onResume,
  onShowKillConfirmation,
  disabled = false,
}) => {
  const styles = useStyles();

  const isPaused = runState === "paused";
  const isPausing = runState === "pausing";

  return (
    <>
      <div className={styles.root} role="toolbar" aria-label="Run controls">
        <div className={styles.spacer} />

        {/* Pause button */}
        {!isPaused && (
          <button
            type="button"
            className={mergeClasses(styles.button, styles.pauseButton)}
            onClick={onPause}
            disabled={disabled || isPausing}
            aria-label={`Pause run${isPausing ? " (in progress)" : ""}`}
          >
            {isPausing ? "Pausing…" : "Pause"}
          </button>
        )}

        {/* Resume button */}
        {isPaused && (
          <button
            type="button"
            className={mergeClasses(styles.button, styles.resumeButton)}
            onClick={onResume}
            disabled={disabled}
            aria-label="Resume run"
          >
            Resume
          </button>
        )}

        {/* Kill button */}
        {onShowKillConfirmation && (
          <button
            type="button"
            className={mergeClasses(styles.button, styles.killButton)}
            onClick={onShowKillConfirmation}
            disabled={disabled}
            aria-label="Kill the run"
          >
            Kill
          </button>
        )}
      </div>
    </>
  );
};

export default SpineControlBar;
